
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Product } from '@/types/product';
import { Loader2 } from 'lucide-react';
import { MarkdownEditor } from '@/components/blog/MarkdownEditor';

interface ProductDialogProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  currentProduct: Product | null;
  setCurrentProduct: (product: Product | null) => void;
  selectedTags: string[];
  onTagSelect: (tagName: string) => void;
  groups: any[];
  tags: any[];
  onSave: () => Promise<void>;
  isSubmitting?: boolean;
}

const ProductDialog = ({
  isOpen,
  setIsOpen,
  currentProduct,
  setCurrentProduct,
  selectedTags,
  onTagSelect,
  groups,
  tags,
  onSave,
  isSubmitting = false,
}) => {
  const { toast } = useToast();
  const [imageState, setImageState] = useState({
    loading: true,
    error: false
  });
  const [inputImageUrl, setInputImageUrl] = useState(currentProduct?.image || '');
  
  useEffect(() => {
    if (currentProduct?.image) {
      setInputImageUrl(currentProduct.image);
    }
  }, [currentProduct?.image]);

  const handleImageUpload = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('type', 'product');
      
      const response = await axios.post('/api/uploads', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Accept': 'application/json'
        },
        withCredentials: true,
        transformRequest: (data) => data
      });
      
      const rawData = response.data;
      const parsedUrl = rawData.url ?? rawData.data?.url;
      if (!parsedUrl) {
        console.error('解析图片 URL 失败，原始响应：', rawData);
        return;
      }
      const imageUrl = parsedUrl;
      let imagePath = imageUrl;
      try {
        imagePath = new URL(imageUrl).pathname;
      } catch (e) {
        // 非完整 URL，保留原始值
      }
      if (currentProduct) {
        const updatedProduct = {
          ...currentProduct,
          image: imagePath
        };
        setCurrentProduct(updatedProduct);
        setInputImageUrl(imagePath);
        setImageState({ loading: false, error: false });
        toast({
          title: "Success",
          description: "Product image uploaded successfully",
          variant: "default"
        });
      }
    } catch (error) {
      console.error('Error uploading product image:', error);
      toast({
        title: "Error",
        description: "Failed to upload product image",
        variant: "destructive"
      });
    }
  };

  const handleIntroductionImageUpload = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('type', 'product');
      
      const response = await axios.post('/api/uploads', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Accept': 'application/json'
        },
        withCredentials: true,
        transformRequest: (data) => data
      });
      
      const rawData = response.data;
      const parsedUrl = rawData.url ?? rawData.data?.url;
      if (!parsedUrl) {
        console.error('解析图片 URL 失败，原始响应：', rawData);
        return;
      }
      
      // Insert the image URL into the markdown editor at cursor position
      const imageMarkdown = `![Image](${parsedUrl})`;
      const currentIntroduction = currentProduct?.introduction || '';
      const newIntroduction = currentIntroduction + '\n' + imageMarkdown;
      
      if (currentProduct) {
        setCurrentProduct({
          ...currentProduct,
          introduction: newIntroduction
        });
      }
      
      toast({
        title: "Success",
        description: "Image uploaded and inserted into introduction",
        variant: "default"
      });
    } catch (error) {
      console.error('Error uploading introduction image:', error);
      toast({
        title: "Error",
        description: "Failed to upload image",
        variant: "destructive"
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {currentProduct && currentProduct.id ? 'Edit Product' : 'Add Product'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={currentProduct?.title || ''}
              onChange={(e) => {
                if (currentProduct) {
                  setCurrentProduct({
                    ...currentProduct,
                    title: e.target.value
                  });
                }
              }}
              disabled={isSubmitting}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={currentProduct?.description || ''}
              onChange={(e) => {
                if (currentProduct) {
                  setCurrentProduct({
                    ...currentProduct,
                    description: e.target.value
                  });
                }
              }}
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="publisher">Publisher</Label>
            <Input
              id="publisher"
              value={currentProduct?.publisher || ''}
              onChange={(e) => {
                if (currentProduct) {
                  setCurrentProduct({
                    ...currentProduct,
                    publisher: e.target.value
                  });
                }
              }}
              disabled={isSubmitting}
              placeholder="Enter publisher name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="introduction">Introduction (Rich Text)</Label>
            <div className="border rounded-md">
              <MarkdownEditor
                value={currentProduct?.introduction || ''}
                onChange={(value) => {
                  if (currentProduct) {
                    setCurrentProduct({
                      ...currentProduct,
                      introduction: value
                    });
                  }
                }}
                onImageUpload={handleIntroductionImageUpload}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="group">Group</Label>
            <Select
              value={currentProduct?.group_id || ''}
              onValueChange={(value) => {
                if (currentProduct) {
                  setCurrentProduct({
                    ...currentProduct,
                    group_id: value || null
                  });
                }
              }}
              disabled={isSubmitting}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a group" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {groups.map((group) => (
                  <SelectItem key={group.id} value={group.id}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label>Tags</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {tags.map((tag) => (
                <div
                  key={tag.id}
                  onClick={() => !isSubmitting && onTagSelect(tag.name)}
                  className={`px-3 py-1 rounded-full text-sm cursor-pointer transition-colors ${
                    selectedTags.includes(tag.name) 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                  } ${isSubmitting ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  {tag.name}
                </div>
              ))}
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="image">Product Image</Label>
            <div className="flex items-center space-x-2">
              <Input
                id="image"
                value={inputImageUrl}
                onChange={(e) => {
                  const url = e.target.value;
                  setInputImageUrl(url);
                  if (currentProduct) {
                    setCurrentProduct({
                      ...currentProduct,
                      image: url
                    });
                  }
                }}
                placeholder="Enter image URL or upload a file"
                className="flex-1"
                disabled={isSubmitting}
              />
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const fileInput = e.target;
                  if (fileInput.files && fileInput.files.length > 0) {
                    const file = fileInput.files[0];
                    handleImageUpload(file);
                  } else {
                    console.error('No file selected');
                  }
                  fileInput.value = '';
                }}
                className="hidden"
                id="product-image-upload"
                disabled={isSubmitting}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('product-image-upload')?.click()}
                disabled={isSubmitting}
              >
                Upload
              </Button>
            </div>
            {currentProduct?.image && (
              <div className="mt-2 space-y-1">
                <div className="relative" style={{minHeight: '80px'}}>
                  <img
                    src={currentProduct.image}
                    alt="Product preview"
                    className="w-32 h-20 object-cover rounded border"
                    style={{display: imageState.error ? 'none' : 'block'}}
                    onLoad={() => {
                      setImageState({ loading: false, error: false });
                    }}
                    onError={(e) => {
                      console.error('Image load failed:', currentProduct.image);
                      setImageState({ loading: false, error: true });
                    }}
                  />
                  {imageState.loading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                  )}
                </div>
                {imageState.error && (
                  <p className="text-xs text-red-500">Failed to load image preview</p>
                )}
              </div>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="site_url">Site URL</Label>
            <Input
              id="site_url"
              value={currentProduct?.site_url || ''}
              onChange={(e) => {
                if (currentProduct) {
                  setCurrentProduct({
                    ...currentProduct,
                    site_url: e.target.value
                  });
                }
              }}
              disabled={isSubmitting}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="is_featured">Featured</Label>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_featured"
                checked={currentProduct?.is_featured || false}
                onChange={(e) => {
                  if (currentProduct) {
                    setCurrentProduct({
                      ...currentProduct,
                      is_featured: e.target.checked
                    });
                  }
                }}
                disabled={isSubmitting}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-500">Show on homepage</span>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="rating">Rating (0-5)</Label>
            <Input
              id="rating"
              type="number"
              min="0"
              max="5"
              step="0.1"
              value={currentProduct?.rating || 0}
              onChange={(e) => {
                if (currentProduct) {
                  setCurrentProduct({
                    ...currentProduct,
                    rating: parseFloat(e.target.value)
                  });
                }
              }}
              disabled={isSubmitting}
            />
          </div>
        </div>
        
        <DialogFooter className="sticky bottom-0 bg-background pt-2 pb-2">
          <Button variant="outline" onClick={() => setIsOpen(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={isSubmitting || !currentProduct?.title}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProductDialog;
